import type {
  GroupColorSlot,
  GroupId,
  GroupsSlice,
  OrderTicketState,
  SymbolGroup,
  WidgetPlacement,
  WorkspaceSliceCreator,
  WorkspaceWidget,
} from "./workspace.types";
import {
  getDefaultGroupColor,
  getNextGroupId,
  normalizeWorkspaceSymbol,
} from "./workspace.utils";

// ---------------------------------------------------------------------------
// Internal types (exported for cross-slice use)
// ---------------------------------------------------------------------------

export type WorkspaceDraft = {
  groups: Partial<Record<GroupId, SymbolGroup>>;
  widgets: Record<string, WorkspaceWidget>;
  tickets: Record<string, OrderTicketState>;
  placements: Record<string, WidgetPlacement>;
};

// ---------------------------------------------------------------------------
// Internal transition helper
// ---------------------------------------------------------------------------

/**
 * Applies a normalized group-symbol update within a single Immer transaction:
 * sets `linkState.symbol` and clears option legs from every grouped Order-Ticket
 * widget whose legs belong to a different underlying symbol. A missing symbol
 * deletes `linkState.symbol` and clears all grouped ticket legs.
 *
 * Must be called from within an Immer `set()` callback. No action may write
 * `linkState.symbol` directly; all writes must go through this helper.
 */
export const applyGroupSymbolTransition = (
  draft: WorkspaceDraft,
  groupId: GroupId,
  symbol?: string,
): void => {
  const group = draft.groups[groupId];
  if (!group) {
    return;
  }

  const normalized = symbol ? normalizeWorkspaceSymbol(symbol) : undefined;
  if (normalized) {
    group.linkState.symbol = normalized;
  } else {
    delete group.linkState.symbol;
  }

  for (const [widgetId, widget] of Object.entries(draft.widgets)) {
    if (widget.groupId === groupId && widget.kind === "orderTicket") {
      const ticket = draft.tickets[widgetId];
      if (ticket) {
        ticket.legs = normalized
          ? ticket.legs.filter(
              (leg) => leg.contract.underlying_symbol === normalized,
            )
          : [];
      }
    }
  }
};

// ---------------------------------------------------------------------------
// Private helpers used by assignWidgetGroup and deleteGroup
// ---------------------------------------------------------------------------

/** Retains `symbol` as the local state for Watchlist/OrderTicket when leaving a group. */
const retainSymbolOnLeave = (
  draft: WorkspaceDraft,
  widgetId: string,
  widget: WorkspaceWidget,
  symbol: string,
): void => {
  if (widget.kind === "watchlist") {
    widget.localSymbol = symbol;
  } else if (widget.kind === "orderTicket") {
    const ticket = draft.tickets[widgetId];
    if (ticket) {
      ticket.localSymbol = symbol;
    }
  }
};

/** Applies a populated destination group symbol to the joining widget. */
export const adoptPopulatedGroupSymbol = (
  draft: WorkspaceDraft,
  widgetId: string,
  widget: WorkspaceWidget,
  newSymbol: string,
): void => {
  if (widget.kind === "watchlist") {
    widget.localSymbol = newSymbol;
  } else if (widget.kind === "orderTicket") {
    const ticket = draft.tickets[widgetId];
    if (ticket) {
      ticket.legs = ticket.legs.filter(
        (leg) => leg.contract.underlying_symbol === newSymbol,
      );
      ticket.localSymbol = newSymbol;
    }
  }
};

const assignWidgetToGroup = (
  draft: WorkspaceDraft,
  widgetId: string,
  groupId: GroupId | undefined,
): void => {
  const widget = draft.widgets[widgetId];
  if (!widget) {
    return;
  }

  const oldGroupId = widget.groupId;
  if (oldGroupId !== null) {
    const retainSymbol = draft.groups[oldGroupId]?.linkState.symbol;
    if (retainSymbol) {
      retainSymbolOnLeave(draft, widgetId, widget, retainSymbol);
    }
  }

  widget.groupId = groupId ?? null;
  if (groupId === undefined) {
    return;
  }
  const newSymbol = draft.groups[groupId]?.linkState.symbol;
  if (newSymbol) {
    adoptPopulatedGroupSymbol(draft, widgetId, widget, newSymbol);
  }
};

// ---------------------------------------------------------------------------
// Slice creator
// ---------------------------------------------------------------------------

export const createGroupsSlice: WorkspaceSliceCreator<GroupsSlice> = (
  set,
  get,
) => ({
  groups: {},

  createAndAssignGroup: (widgetId) => {
    const state = get();
    const existingIds = Object.keys(state.groups) as GroupId[];
    let id = getNextGroupId(existingIds);
    if (id === undefined) {
      const occupiedIds = Object.values(state.widgets)
        .map((widget) => widget.groupId)
        .filter((groupId): groupId is GroupId => groupId !== null);
      id = getNextGroupId(occupiedIds);
    }
    if (id === undefined) {
      return undefined;
    }

    const widget = state.widgets[widgetId];
    const seedSymbol =
      widget?.kind === "watchlist"
        ? widget.localSymbol
        : widget?.kind === "orderTicket"
          ? state.tickets[widgetId]?.localSymbol
          : undefined;

    set((draft) => {
      if (!draft.groups[id]) {
        draft.groups[id] = {
          id,
          colorSlot: getDefaultGroupColor(id),
          linkState: {},
        };
      }
      if (seedSymbol && !draft.groups[id]?.linkState.symbol) {
        applyGroupSymbolTransition(draft, id, seedSymbol);
      }
      assignWidgetToGroup(draft, widgetId, id);
    });

    return id;
  },

  assignWidgetGroup: (widgetId, groupId) => {
    set((draft) => {
      assignWidgetToGroup(draft, widgetId, groupId);
    });
  },

  assignWidgetsGroup: (widgetIds, groupId) => {
    set((draft) => {
      for (const widgetId of widgetIds) {
        assignWidgetToGroup(draft, widgetId, groupId);
      }
    });
  },

  setGroupSymbol: (groupId, symbol) => {
    set((draft) => {
      applyGroupSymbolTransition(draft, groupId, symbol);
    });
  },

  setGroupColor: (groupId, colorSlot: GroupColorSlot) => {
    set((draft) => {
      const group = draft.groups[groupId];
      if (group) {
        group.colorSlot = colorSlot;
      }
    });
  },

  setGroupName: (groupId, name) => {
    set((draft) => {
      const group = draft.groups[groupId];
      if (!group) {
        return;
      }
      const trimmed = name.trim();
      if (trimmed) {
        group.name = trimmed;
      } else {
        delete group.name;
      }
    });
  },

  setGroupDateRange: (groupId, range) => {
    set((draft) => {
      const group = draft.groups[groupId];
      if (!group) {
        return;
      }
      if (range === undefined) {
        delete group.linkState.dateRange;
      } else {
        group.linkState.dateRange = { from: range.from, to: range.to };
      }
    });
  },

  deleteGroup: (groupId) => {
    set((draft) => {
      const retainSymbol = draft.groups[groupId]?.linkState.symbol;

      for (const [widgetId, widget] of Object.entries(draft.widgets)) {
        if (widget.groupId !== groupId) {
          continue;
        }
        if (retainSymbol) {
          retainSymbolOnLeave(draft, widgetId, widget, retainSymbol);
        }
        widget.groupId = null;
      }
      delete draft.groups[groupId];
    });
  },
});
